import Cookie from 'js-cookie'
import { isBrowser } from '@process'

// Actions
import { actions as authActions } from 'modules/auth/actions'

// Schemas
import { profileSchema, inviteSchema, profileListSchema } from 'modules/profile/models'

// Types
import { APIAction, Dispatch, Profile, GetState } from 'types'

// Utils
import jsTracking from 'lib/jsTracking'

export const actions = {
  avatarSave: (file: string, profileId: number) => (dispatch: Dispatch) =>
    dispatch(actions._avatarSave(file, profileId)).then(() => dispatch(actions.fetchProfile())),

  _avatarSave: (file: string, profileId: number): APIAction => ({
    CALL_API: {
      type: 'AVATAR_SAVE',
      method: 'PUT',
      endpoint: `/api/profile/avatar/${profileId}/`,
      schema: profileSchema,
      data: {
        avatar: file,
      },
    },
  }),

  changePassword: (data: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'PROFILE_PW',
      method: 'POST',
      endpoint: '/api/auth/password/change/',
      schema: profileSchema,
      data,
    },
  }),

  _updateProfileData: (profile: Partial<Profile>, profileId: number): APIAction => {
    const { subscription, avatar, ...modifiedProfile } = profile // eslint-disable-line
    return {
      CALL_API: {
        type: 'PROFILE_UPDATE',
        method: 'PATCH',
        endpoint: `/api/profile/${profileId}/`,
        schema: profileSchema,
        data: { ...modifiedProfile, avatar_img: avatar },
      },
    }
  },

  sendInvite: (email: string) => (dispatch: Dispatch) =>
    dispatch(actions._sendInvite(email)).then(resp => {
      jsTracking('Invite', {
        eventCategory: 'Invites',
        eventAction: 'send_invite',
        eventLabel: 'email',
      })
      return resp
    }),

  _sendInvite: (email: string): APIAction => ({
    CALL_API: {
      type: 'INVITE_SEND',
      method: 'POST',
      endpoint: '/api/invitation/',
      data: { email },
      schema: inviteSchema,
    },
  }),

  deleteProfile: (userId: number) => (dispatch: Dispatch) =>
    dispatch(actions._deleteProfile(userId)).then(() => setTimeout(() => dispatch(authActions.logout()), 1000)),

  _deleteProfile: (userId: number) => ({
    CALL_API: {
      type: 'PROFILE_DELETE',
      method: 'POST',
      endpoint: '/api/profile/remove/',
      throwSubmitException: true,
      schema: profileSchema,
      data: { userId },
    },
  }),

  fetchProfile: () => ({
    CALL_API: {
      type: 'FETCH_PROFILE',
      method: 'GET',
      endpoint: '/api/profile/',
      schema: profileListSchema,
    },
  }),

  updateProfile: (data: Partial<Profile>, profileId: number) => (dispatch: Dispatch) => {
    dispatch({
      type: 'PROFILE_UPDATE',
    })
    if (isBrowser) {
      const { theme } = data as Profile
      if (['light', 'dark'].includes(theme)) {
        Cookie.set('theme', theme)
      } else {
        Cookie.remove('theme')
      }
    }

    return dispatch(actions._updateProfileData(data, profileId))
  },

  removeSavedPaymentMethod: (): APIAction => ({
    CALL_API: {
      type: 'REMOVE_SAVED_PAYMENT_METHOD',
      method: 'POST',
      endpoint: '/api/profile/removesavedpaymentmethod/',
      schema: profileSchema,
    },
  }),

  addEmail: (email: string): APIAction => ({
    CALL_API: {
      type: 'ADD_EMAIL',
      method: 'POST',
      endpoint: '/api/profile/addemail/',
      schema: profileSchema,
      data: { email },
    },
  }),

  deleteEmail: (email: string): APIAction => ({
    CALL_API: {
      type: 'DELETE_EMAIL',
      method: 'POST',
      endpoint: '/api/profile/deleteemail/',
      schema: profileSchema,
      data: { email },
    },
  }),

  setPrimaryEmail: (email: string): APIAction => ({
    CALL_API: {
      type: 'SET_PRIMARY_EMAIL',
      method: 'POST',
      endpoint: '/api/profile/setprimaryemail/',
      schema: profileSchema,
      data: { email },
    },
  }),

  toggleEmailNotifications: (enabled: boolean) => (dispatch: Dispatch, getState: GetState) => {
    const { profileId } = getState().auth
    return dispatch(actions.toggleEmailNotificationsCall(enabled, profileId))
  },

  toggleEmailNotificationsCall: (enabled: boolean, profileId: number): APIAction => ({
    CALL_API: {
      type: 'TOGGLE_EMAIL_NOTIFICATION',
      method: 'PATCH',
      endpoint: `/api/profile/${profileId}/`,
      schema: profileSchema,
      data: { is_unsubscribed: !enabled },
    },
  }),

  updateSocials: (data: Partial<Profile>, profileId: number): APIAction => ({
    CALL_API: {
      type: 'UPDATE_SOCIALS',
      method: 'PATCH',
      endpoint: `/api/profile/${profileId}/`,
      schema: profileSchema,
      data,
    },
  }),

  acceptTerms: (): APIAction => ({
    CALL_API: {
      type: 'ACCEPT_TERMS',
      method: 'POST',
      endpoint: '/api/profile/acceptterms/',
      schema: profileSchema,
    },
  }),
}
